import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { GatewayTicketModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tickets/models/outputs/gateway-ticket.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetTicketQuery } from '@service/cpms-bo-ticket.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

import { TicketUpdateController } from '../ticket-update/ticket-update.controller';
import { UpdatePriorityComponent } from '../ticket-update/update-priority.component';
import { UpdateStatusComponent } from '../ticket-update/update-status.component';
import { TicketContextProvider } from '../ticket.context';
import { TicketDetailsView } from './ticket-details.view';

interface TicketDetailsControllerProps {
  resource: GatewayTicketModel;
}

const TicketDetailsController = ({
  resource,
}: TicketDetailsControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  // Nothing to do

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.title);
  }, [resource.title, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    setPageAction([
      <UpdateStatusComponent
        key="status"
        status={resource.status}
        id={resource.id}
      />,
      <UpdatePriorityComponent
        key="priority"
        priority={resource.priority}
        id={resource.id}
      />,
      <ButtonIcon
        key="edit"
        icon="edit-05"
        onClick={() => setIsEditDialogOpen(true)}
      />,
    ]);
  }, [resource.status, resource.priority, resource.id, setPageAction]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <TicketContextProvider data={resource}>
      <TicketDetailsView />
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        confirmClose
        height="100%"
        size={DialogSize.LARGE}>
        <Dialog.Header
          icon="edit-01"
          onClose={() => setIsEditDialogOpen(false)}
          title={t('$.ticket-update.title')}
        />
        <Dialog.Content>
          <TicketUpdateController
            ticket={resource}
            onCancel={() => setIsEditDialogOpen(false)}
            onSuccess={() => setIsEditDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
    </TicketContextProvider>
  );
};

export const TicketDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetTicketQuery(params.id ?? '', {
    skip: !params.id,
  });

  const { Component } = withProtectedResource<
    GatewayTicketModel,
    {},
    GatewayTicketModel
  >(TicketDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.TICKETS,
        action: ActionsByResource.TICKETS.READ,
      }),
  })({});

  return Component;
};
